import React, { ComponentPropsWithoutRef, Fragment, useCallback } from 'react';
import { Typography } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import useConfirmDialog from '@modules/ui/dialog/useConfirmDialog';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import { resolveStaticHref, showYouAreLeavingRobloxWarning } from '../utils/staticLinkUtils';
import openInNewTab from '../utils/openInNewTab';
import useMarkdownLinkStyles from './MarkdownLink.styles';

export type MarkdownLinkProps = ComponentPropsWithoutRef<typeof DynamicallyRouted.Link> & {
  linkStyle?: React.CSSProperties | Record<string, unknown>;
};

export default function MarkdownLink({
  href,
  // pluck the non-standard `color` HTML attribute out since it's causing incompatibility
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  color: _,
  linkStyle,
  children,
  ...otherProps
}: MarkdownLinkProps) {
  const { translate, translateHTML } = useTranslation();
  const {
    classes: { wordWrap, root },
  } = useMarkdownLinkStyles();

  const { showConfirmDialog } = useConfirmDialog();

  const showExternalLinkDialog = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      event.preventDefault();
      showConfirmDialog({
        title: translate('Title.LeavingRoblox'),
        cancelButtonText: translate('Label.Cancel'),
        confirmButtonText: translate('Label.Continue'),
        confirmDialogChildren: (
          <Fragment>
            {translateHTML('Description.RedirectConfirmationWrapper', [
              {
                opening: 'redirectLinkStart',
                closing: 'redirectLinkEnd',
                // eslint-disable-next-line react/no-unstable-nested-components
                content: () => (
                  <Typography className={wordWrap} display='inline' variant='body1'>
                    {href}
                  </Typography>
                ),
              },
            ])}
            <br />
            <br />
            <Typography>{translate('Description.RedirectDisclaimer')}</Typography>
          </Fragment>
        ),
        maxWidth: 'Medium',
        onConfirm: () => {
          openInNewTab(href);
        },
        onCancel: () => {
          // do nothing
        },
      });
      return false;
    },
    [href, showConfirmDialog, translate, translateHTML, wordWrap],
  );
  // if the link is external (not valid), showExternalLinkDialog
  if (href && showYouAreLeavingRobloxWarning(href)) {
    return (
      <DynamicallyRouted.Link
        href={href}
        rel='nofollow'
        onClick={showExternalLinkDialog}
        {...otherProps}>
        <Typography className={root} variant='body1' style={linkStyle}>
          {children}
        </Typography>
      </DynamicallyRouted.Link>
    );
  }

  return (
    <DynamicallyRouted.Link href={resolveStaticHref(href)} {...otherProps}>
      <Typography className={root} variant='body1' style={linkStyle}>
        {children}
      </Typography>
    </DynamicallyRouted.Link>
  );
}
